import { useState } from "react";
import Header from "@/components/Header";
import ContactSection from "@/components/ContactSection";
import Footer from "@/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Play, User, Mic, Settings } from "lucide-react";

const Videoavatarer = () => {
  const { language, setLanguage, content: currentContent } = useLanguage();
  const videoAvatarsContent = currentContent.videoAvatars;

  return (
    <div className="min-h-screen bg-background">
      <Header 
        language={language} 
        onLanguageChange={setLanguage}
        content={currentContent.header}
      />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-20 px-4">
          <div className="container mx-auto max-w-4xl text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              <span className="hero-text">{videoAvatarsContent.hero.title}</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
              {videoAvatarsContent.hero.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild 
                size="lg" 
                className="bg-gradient-primary border-0 hover:opacity-90"
              >
                <a 
                  href="https://volym.typeform.com/to/vvm1IndH?utm_source=xxxxx&utm_campaign=xxxxx&utm_content=xxxxx" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  {videoAvatarsContent.hero.cta.primary}
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 px-4 bg-secondary/30">
          <div className="container mx-auto max-w-6xl">
            <div className="grid md:grid-cols-3 gap-8">
              {videoAvatarsContent.features.map((feature, index) => (
                <div key={index} className="text-center p-6">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                    {index === 0 && <User className="w-6 h-6 text-primary" />}
                    {index === 1 && <Mic className="w-6 h-6 text-primary" />}
                    {index === 2 && <User className="w-6 h-6 text-primary" />}
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-20 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">{videoAvatarsContent.pricing.title}</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                {videoAvatarsContent.pricing.subtitle}
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {videoAvatarsContent.pricing.packages.map((pkg, index) => (
                <div 
                  key={index}
                  className={`bg-card rounded-xl border p-6 shadow-sm relative ${
                    pkg.popular ? 'border-2 border-primary shadow-lg' : ''
                  }`}
                >
                  {pkg.popular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className="bg-gradient-primary text-white px-4 py-1 rounded-full text-sm font-medium">
                        {language === 'sv' ? 'MEST POPULÄR' : 'MOST POPULAR'}
                      </span>
                    </div>
                  )}
                  
                  <div className={`text-center mb-6 ${pkg.popular ? 'mt-4' : ''}`}>
                    <h3 className="text-2xl font-bold mb-2">{pkg.name}</h3>
                    <p className="text-muted-foreground">{pkg.subtitle}</p>
                  </div>
                  
                  <div className="space-y-4 mb-6">
                    {pkg.features.map((feature, featureIndex) => (
                      <div 
                        key={featureIndex}
                        className={`flex justify-between items-center p-3 border rounded-lg ${
                          featureIndex === 2 ? 'bg-secondary/10' : ''
                        }`}
                      >
                        <div>
                          <span className="font-medium text-sm">{feature.name}</span>
                          <p className="text-xs text-muted-foreground">{feature.description}</p>
                        </div>
                        <span className="text-lg font-bold">{feature.price}</span>
                      </div>
                    ))}
                  </div>
                  
                  <Button 
                    asChild 
                    className="w-full bg-gradient-primary border-0 hover:opacity-90"
                  >
                    <a 
                      href="https://volym.typeform.com/to/vvm1IndH?utm_source=xxxxx&utm_campaign=xxxxx&utm_content=xxxxx" 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      {pkg.cta}
                    </a>
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </section>

        <ContactSection content={currentContent.contact} />
      </main>
      
      <Footer 
        content={{
          company: "Volym Digitalbyrå AB",
          rights: language === "sv" ? "Alla rättigheter förbehållna." : "All rights reserved."
        }}
      />
    </div>
  );
};

export default Videoavatarer;